import axios, { AxiosResponse } from 'axios';
import { KEY_LOCALSTORAGE } from '../../auth/core/_consts';
import { OctResponse } from '../../models/OctResponse';
import { localStorageItem } from '../../utils/LocalStorage';
import { paramsConfig } from '../../utils/ParamsUtils';
import {
	PatientXmlListSearchParams,
	PhoiBaoHiemSearchParams,
	sendXMLResponse,
	validateXMLParams,
	DigitalSignatureResDto,
	SendSignedXMLParams,
} from '../types';
import { APP_CONFIGURATION_KEYS } from '../../utils/Constant';

const BASE_URL =
	localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[APP_CONFIGURATION_KEYS.API_URL] ||
	process.env.REACT_APP_API_URL;

const INS_URL =
	localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[APP_CONFIGURATION_KEYS.INS_URL];

const INS_URL_3176 =
	localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[APP_CONFIGURATION_KEYS.INS_URL_3176];

// Global state để lưu version hiện tại
let currentInsVersion: '4750' | '3176' = '4750';

// Hàm để set version từ component
export const setCurrentInsVersion = (version: '4750' | '3176') => {
	currentInsVersion = version;
};

// Hàm để get version hiện tại
export const getCurrentInsVersion = () => currentInsVersion;

// Helper function to get URL based on current version
export const getInsUrl = () => {
	// return currentInsVersion === '3176' ? INS_URL_3176 : INS_URL;
	return INS_URL;
};

export const getPatientInfo = async (params: validateXMLParams) => {
	const { data } = await axios.post<OctResponse<any>>(
		`${INS_URL}/ins-visit/patient-info`,
		params
	);
	return data;
};

export const getInsuranceServices = async (paymentCode: string) => {
	const { data } = await axios.get<OctResponse<any>>(
		`${BASE_URL}/ins-visit/visit-code/${paymentCode}/payment-info`
	);
	return data;
};

export const getInsuranceInvoices = async (params: validateXMLParams) => {
	const { visitCode } = params;
	const { data } = await axios.get<OctResponse<any>>(
		`${INS_URL}/ins-visit/visit-code/${visitCode}/invoices`
	);
	return data;
};

export const getInsuranceInvoiceDetail = async (invoiceId: string) => {
	const { data } = await axios.get<OctResponse<any>>(`${BASE_URL}/ins-visit/inv/${invoiceId}`);
	return data;
};

export const approveInsurance = async (id: string) => {
	const { data } = await axios.post<OctResponse<any>>(`${INS_URL}/ins-visit/${id}/approve`);
	return data;
};

export const cancelApproveInsurance = async (id: string) => {
	const { data } = await axios.post<OctResponse<any>>(
		`${INS_URL}/ins-visit/${id}/cancel-approval`
	);
	return data;
};

export const getXmlData = async (
	xmlTabCode: string,
	params: {
		patientCode: string;
		visitCode: string;
	}
) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.get<OctResponse<any>>(
		`${baseUrl}/xml-processor/type/${xmlTabCode}`,
		paramsConfig(params)
	);
	return data;
};

export const validateXML = async (params: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<OctResponse<any>>(`${baseUrl}/xml/validate`, params);
	return data;
};

export const exportInsuranceXML = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post(`${baseUrl}/xml/summary`, requestBody);
	return data;
};

export const exportInsuranceXMLBatch = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post(`${baseUrl}/xml/summary-async`, requestBody);
	return data;
};

export const gettXmlTabs = async (params: { patientCode: string; visitCode: string }) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.get<OctResponse<any>>(
		`${baseUrl}/xml-processor/existing-xml`,
		paramsConfig(params)
	);
	return data;
};

export const downloadXML = async (params: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const form = new FormData();
	Object.entries(params).forEach(([key, value]) => {
		form.set(key, value as string);
	});
	const response = await axios.post(`${baseUrl}/xml-processor/download`, form);
	return response;
};

export const exportCheckinXML = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post(`${baseUrl}/xml/check-in-encode`, requestBody);
	return data;
};

export const sendXMLCheckIn = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<OctResponse<sendXMLResponse>>(
		`${baseUrl}/egw/checkin/send`,
		requestBody
	);
	return data;
};

export const sendXMLCheckIns = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<OctResponse<sendXMLResponse>>(
		`${baseUrl}/egw/checkin/sends`,
		requestBody
	);
	return data;
};

export const sendXMLPayment = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<OctResponse<sendXMLResponse>>(
		`${baseUrl}/egw/payment/send`,
		requestBody
	);
	return data;
};

export const sendXMLPayments = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<OctResponse<sendXMLResponse>>(
		`${baseUrl}/egw/payment/sends`,
		requestBody
	);
	return data;
};

export const getPatientXmlList = async (params: PatientXmlListSearchParams) => {
	const { data } = await axios.post<OctResponse<any>>(`${INS_URL}/ins/list`, params);
	return data;
};

export const getPatientXmlMenuTree = async (params: PatientXmlListSearchParams) => {
	const { data } = await axios.post<OctResponse<any>>(
		`${INS_URL}/ins/list/left-menu-tree`,
		params
	);
	return data;
};

export const getPhoiBaoHiemList = async (params: PhoiBaoHiemSearchParams) => {
	const { data } = await axios.post<OctResponse<any>>(
		`${INS_URL}/ins/list/insurance-settlement`,
		params
	);
	return data;
};

export const getCheckinList = async (params: PatientXmlListSearchParams) => {
	const { data } = await axios.post<OctResponse<any>>(
		`${INS_URL}/ins/list/check-in`,
		params
	);
	return data;
};
export const getXMLFilesList = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const response = await axios.post<OctResponse<{
		profileLinkCodes: string[];
		files: Record<string, string>; // fileName -> base64 XML content
	}>>(
		`${baseUrl}/xml-processor/files-xml`,
		requestBody
	);

	const { files, profileLinkCodes } = response.data.data || { files: {}, profileLinkCodes: [] };

	const xmlFilesList = Object.entries(files).map(([fileName, base64Content], index) => {
		// Keep as base64 - SignServerTools will handle decode/sign/encode
		const xmlContent = base64Content;

		// Extract profileLinkCode from fileName or use from array
		// fileName format: "25003706_LK00380125_20251226151709.xml"
		const profileLinkCode = profileLinkCodes[index] || fileName.split('_').slice(0, 2).join('_');

		return {
			xmlContent,
			fileName,
			profileLinkCode,
		};
	});

	return {
		...response.data,
		data: xmlFilesList,
	};
};

/**
 * API gửi XML đã ký lên BE
 * @param requestBody - SendSignedXMLParams chứa fileName và signature (XML đã ký)
 * @returns Response từ BE
 */
export const sendSignedXMLPayment = async (requestBody: SendSignedXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<OctResponse<sendXMLResponse>>(
		`${baseUrl}/egw/payment/send-signed`,
		requestBody
	);
	return data;
};

/**
 * API gửi hàng loạt XML đã ký lên BE
 * @param mapSignatureContent - Object chứa mapping fileName -> base64 signed XML
 * @returns Response từ BE
 */
export const sendSignedXMLBatch = async (mapSignatureContent: Record<string, string>) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<OctResponse<sendXMLResponse>>(
		`${baseUrl}/egw/payment/send-signed-batch`,
		{ mapSignatureContent }
	);
	return data;
};

// Export category services
export * from './categoryService';

/**
 * ============================================
 * XML PROCESSOR APIs - SSE-based export flow
 * ============================================
 */

/**
 * Step 1: Khởi tạo job export XML
 * @param requestBody - validateXMLParams chứa patientCodes, visitCodes, profileLinkCodes
 * @returns Job ID để theo dõi tiến trình
 */
export const initXmlExportJob = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<string>(
		`${baseUrl}/xml-processor/export`,
		requestBody,
		{ timeout: 5 * 60 * 1000 } // 5 phút - override timeout mặc định
	);
	return data;
};

/**
 * Step 2: Theo dõi tiến trình export qua SSE
 * SSE endpoint: GET /xml-processor/progress/{id}
 * Note: Sử dụng EventSource trực tiếp trong hook useSSEProgress
 */

/**
 * Step 3: Download file XML sau khi export hoàn tất
 * @param jobId - Job ID nhận được từ initXmlExportJob
 * @returns Axios response chứa file data và headers
 */
export const downloadXmlByJobId = async (jobId: string): Promise<AxiosResponse<Blob>> => {
	const baseUrl = getInsUrl();
	const response = await axios.get(`${baseUrl}/xml-processor/download/${jobId}`, {
		responseType: 'blob',
	});
	return response;
};

/**
 * === Validate XML Flow với SSE ===
 */

/**
 * Step 1: Khởi tạo job validate XML
 * @param requestBody - Payload chứa danh sách patient visits cần validate
 * @returns Job ID để theo dõi tiến trình validate
 */
export const initXmlValidateJob = async (requestBody: validateXMLParams) => {
	const baseUrl = getInsUrl();
	const { data } = await axios.post<string>(
		`${baseUrl}/xml/validate/export`,
		requestBody,
		{ timeout: 5 * 60 * 1000 } // 5 phút - override timeout mặc định
	);
	return data;
};

/**
 * Step 2: Theo dõi tiến trình validate qua SSE
 * SSE endpoint: GET /xml/validate/progress/{id}
 * Note: Sử dụng EventSource trực tiếp trong hook useSSEProgress
 */

/**
 * Step 3: Download file kết quả validate sau khi hoàn tất
 * @param jobId - Job ID nhận được từ initXmlValidateJob
 * @returns Axios response chứa file data và headers
 */
export const downloadXmlValidateResult = async (jobId: string): Promise<AxiosResponse<Blob>> => {
	const baseUrl = getInsUrl();
	const response = await axios.get(`${baseUrl}/xml/validate/download-excel/${jobId}`, {
		responseType: 'blob',
	});
	return response;
};

/**
 * Step 4: Lấy kết quả validate để hiển thị trên popup
 * @param jobId - Job ID nhận được từ initXmlValidateJob
 * @returns Chuỗi kết quả lỗi validate (multiline string)
 */
export const getXmlValidateResult = async (jobId: string): Promise<any> => {
	const baseUrl = getInsUrl();
	const { data } = await axios.get<OctResponse<any>>(`${baseUrl}/xml/validate/${jobId}`);
	return data;
};